import { useMemo } from "react";
import { useSettingsStore } from "../../../stores/index.js";
import { mixColors } from "../../task-colors.js";
import { useTopicRailModel } from "../../topics/rail-model.js";
import { paperTint } from "./palette.js";
import type { RailTopic } from "./TopicRail.js";

export interface TopicRailState {
  rail: RailTopic[];
  /** The rail's selected ROOT topic (the design picks one main topic). */
  selectedRoot: RailTopic | null;
  selectedRootIds: string[];
  selectedIndex: number;
  topicWash: string | null;
  /** Selected subtopic ids visible in the rail. */
  subSel: string[];
  selectRoot: (rootId: string) => void;
  toggleSub: (subId: string) => void;
}

/** Maps the app's free-form `topic_ids` (many topics, nested) onto the design's
 *  rail model: one selected root topic + any of its direct children. Ids that
 *  the rail doesn't show (deeper descendants, children of other roots) are left
 *  untouched in `topic_ids`. */
export function useTopicRail(
  topicIds: string[],
  setTopicIds: (ids: string[]) => void,
): TopicRailState {
  const model = useTopicRailModel();
  const multi = useSettingsStore((s) => s.mobileTopicMulti);

  const rail = useMemo<RailTopic[]>(
    () => model.map((r) => ({
      id: r.id,
      name: r.name,
      color: paperTint(r.color),
      icon: r.icon,
      children: r.children.map((c) => ({
        id: c.id,
        name: c.name,
        color: paperTint(c.color),
        icon: c.icon,
      })),
    })),
    [model],
  );

  const selected = new Set(topicIds);
  const rootForId = new Map<string, RailTopic>();
  const colorForId = new Map<string, string>();
  for (const root of rail) {
    rootForId.set(root.id, root);
    colorForId.set(root.id, root.color);
    for (const child of root.children) {
      rootForId.set(child.id, root);
      colorForId.set(child.id, child.color);
    }
  }

  const selectedRoot =
    topicIds.map((id) => rootForId.get(id)).find(Boolean)
    ?? rail.find((r) => selected.has(r.id))
    ?? rail.find((r) => r.children.some((c) => selected.has(c.id)))
    ?? null;
  const selectedRootIds = rail
    .filter((r) => selected.has(r.id) || r.children.some((c) => selected.has(c.id)))
    .map((r) => r.id);
  const selectedIndex = selectedRoot ? rail.indexOf(selectedRoot) : -1;
  const subSel = rail.flatMap((root) => root.children.filter((c) => selected.has(c.id)).map((c) => c.id));
  const selectedColors = topicIds.map((id) => colorForId.get(id)).filter(Boolean) as string[];
  const topicWash = mixColors(selectedColors);

  const selectRoot = (rootId: string) => {
    const root = rail.find((r) => r.id === rootId);
    if (!root) return;
    if (selectedRootIds.includes(rootId)) {
      // Deselect: drop the root and its (rail-visible) children.
      const drop = new Set([rootId, ...root.children.map((c) => c.id)]);
      setTopicIds(topicIds.filter((id) => !drop.has(id)));
      return;
    }
    if (multi) {
      setTopicIds([rootId, ...topicIds.filter((id) => id !== rootId)]);
      return;
    }
    // Switch: drop every rail root + the previous root's children, keep the rest.
    const drop = new Set<string>();
    rail.forEach((r) => {
      drop.add(r.id);
      r.children.forEach((c) => drop.add(c.id));
    });
    setTopicIds([rootId, ...topicIds.filter((id) => !drop.has(id))]);
  };

  const toggleSub = (subId: string) => {
    setTopicIds(
      topicIds.includes(subId) ? topicIds.filter((id) => id !== subId) : [...topicIds, subId],
    );
  };

  return { rail, selectedRoot, selectedRootIds, selectedIndex, topicWash, subSel, selectRoot, toggleSub };
}
